import { z } from "zod";
/**
 * Wire-format DTOs for the admin Companies + Company Locations CRUD
 * (`/v1/admin/companies/*`, P08-T2).
 *
 * Field naming follows the existing admin-controller convention: snake_case
 * keys mirroring database columns (see `packages/db/src/schema/org.ts`).
 *
 * Address shape note: `company_locations.address_json` is a JSONB column with
 * the documented shape `{ line1, line2?, city, postcode, country }`. Request
 * and DTO schemas adopt those JSON keys verbatim — see locations.ts for the
 * matching storefront DTO and the rationale (1:1 deserialisation, no
 * translation surface).
 */
// ---------- Enums ----------
export const CompanyTierValues = [
    "standard",
    "bronze",
    "silver",
    "gold",
];
export const CompanyTier = z.enum(CompanyTierValues);
export const CompanyApprovalStatusValues = [
    "pending",
    "approved",
    "rejected",
];
export const CompanyApprovalStatus = z.enum(CompanyApprovalStatusValues);
export const CompanyPaymentTermsValues = [
    "immediate",
    "net_30",
    "net_60",
    "cod",
];
export const CompanyPaymentTerms = z.enum(CompanyPaymentTermsValues);
export const CompanyLocationPaymentMethodValues = [
    "card",
    "bank_transfer",
    "credit_terms",
];
export const CompanyLocationPaymentMethod = z.enum(CompanyLocationPaymentMethodValues);
// ---------- Address shape (matches address_json JSONB column) ----------
export const CompanyLocationAddress = z.object({
    line1: z.string().min(1).max(200),
    line2: z.string().max(200).nullable().optional(),
    city: z.string().min(1).max(100),
    postcode: z.string().min(1).max(20),
    country: z.string().length(2),
});
// ---------- Request bodies ----------
const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const CreateCompanyBody = z.object({
    name: z.string().min(1).max(200),
    slug: z.string().min(1).max(120).regex(slugRegex, "slug must be kebab-case"),
    vat_number: z.string().max(64).nullable().optional(),
    vat_country: z.string().length(2).default("GB"),
    default_tier: CompanyTier.default("standard"),
    approval_status: CompanyApprovalStatus.default("pending"),
    payment_terms: CompanyPaymentTerms.default("immediate"),
    credit_limit_minor: z.coerce.number().int().min(0).nullable().optional(),
    // P17-T8: per-company COD eligibility gate. Defaults to false at the
    // DB-default boundary; explicit `true` here only when admin opts-in.
    cod_enabled: z.boolean().optional(),
});
// .partial() preserves .default(...), which would silently reset fields on an
// empty PATCH. Re-declare the defaulted fields as plain optionals.
//
// PATCH semantics: fields accept `null` to explicitly clear (where the column
// is nullable, e.g. `vat_number`, `credit_limit_minor`), or `undefined` to
// leave the field unchanged. The service-layer `if (input.x !== undefined)`
// guard preserves this distinction — a missing key skips the update; an
// explicit `null` writes `null` to the column.
export const UpdateCompanyBody = CreateCompanyBody.partial().extend({
    vat_country: z.string().length(2).optional(),
    default_tier: CompanyTier.optional(),
    approval_status: CompanyApprovalStatus.optional(),
    payment_terms: CompanyPaymentTerms.optional(),
});
export const CreateCompanyLocationBody = z.object({
    name: z.string().min(1).max(200),
    code: z.string().min(1).max(64),
    discount_percent: z.coerce.number().int().min(0).max(100).default(0),
    address: CompanyLocationAddress,
    allowed_payment_methods: z
        .array(CompanyLocationPaymentMethod)
        .default([]),
    buyer_can_edit_address: z.boolean().default(false),
});
export const UpdateCompanyLocationBody = CreateCompanyLocationBody.partial().extend({
    discount_percent: z.coerce.number().int().min(0).max(100).optional(),
    allowed_payment_methods: z
        .array(CompanyLocationPaymentMethod)
        .optional(),
    buyer_can_edit_address: z.boolean().optional(),
});
export const ListCompaniesQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().max(200).optional(),
});
